/**
 * Demo Phone
 * One of the homepage's demos on a phone: its first frame as a picture,
 * its recording playing over it, the phone's foot fading away
 */

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DEMOS, type Demo } from '@components/landing/demo-section';
import { FadingPhone } from '@components/landing/phone-fade';
import { colors } from '@config/colors';

/*
 * Shared by the slides that show a demo (Demo; DTemplate's examples).
 * Every phone shows its recording's first frame as a picture, the
 * recording laid over it: a recording that won't load or play leaves the
 * picture, never an empty phone. `startAfter` null — still (reduced
 * motion, or `?capture=1`, how decks are exported) — the picture alone,
 * which the export waits for as it waits for every image.
 *
 * Sized by `width` (canvas pixels): the bezel, its corners and the
 * screen's are the Demo slide's at 224px, in proportion.
 *
 * `still`, when given, is the frame a phone shows still in place of the
 * first one (DTemplate's, from later in each recording, where the answer
 * is on screen). A moving phone keeps the first frame under its
 * recording, so the recording starts over it seamlessly.
 */

export type DemoScreen = NonNullable<Demo['screen']>;

/** The homepage's demos with a recording — its live ones, not "soon". */
export type LiveDemo = Demo & { screen: DemoScreen };
export const LIVE_DEMOS: readonly LiveDemo[] = DEMOS.filter((demo): demo is LiveDemo => !demo.soon && Boolean(demo.screen));

export const DemoPhone: React.FC<{ screen: DemoScreen; startAfter: number | null; width?: number; still?: string }> = ({
  screen,
  startAfter,
  width = 224,
  still,
}) => {
  const k = width / 224;
  return (
    <FadingPhone>
      <Phone style={{ width, borderRadius: 34 * k, padding: 6 * k }}>
        <Screen screen={screen} startAfter={startAfter} radius={28 * k} still={still} />
      </Phone>
    </FadingPhone>
  );
};

/** The first frame and, moving, the recording over it — started
 *  `startAfter` seconds after the slide arrives, looping. A recording
 *  that fails is taken away, the picture left. */
const Screen: React.FC<{ screen: DemoScreen; startAfter: number | null; radius: number; still?: string }> = ({
  screen,
  startAfter,
  radius,
  still,
}) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  const moving = startAfter !== null && !failed;
  useEffect(() => {
    const video = ref.current;
    if (!video || startAfter === null) return undefined;
    const id = window.setTimeout(() => {
      video.currentTime = 0;
      // A browser that won't play it leaves the picture showing.
      video.play().catch(() => undefined);
    }, startAfter * 1000);
    return () => window.clearTimeout(id);
  }, [startAfter]);
  return (
    <ScreenFrame style={{ borderRadius: radius }}>
      <img src={moving ? screen.poster : (still ?? screen.poster)} alt={moving ? '' : screen.label} width={540} height={1174} />
      {moving && (
        <video
          ref={ref}
          src={screen.video}
          aria-label={screen.label}
          width={540}
          height={1174}
          muted
          playsInline
          loop
          preload="auto"
          onError={() => setFailed(true)}
        />
      )}
    </ScreenFrame>
  );
};

const Phone = styled.div`
  aspect-ratio: 540 / 1174;
  background: ${colors.neutral.extraDark};
  overflow: hidden;
`;

/* The first frame, and the recording laid exactly over it. */
const ScreenFrame = styled.div`
  position: relative;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: auto;
  }

  video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
